import React from 'react';
import { NavLink } from 'react-router-dom';
import { Home as HomeIcon } from 'lucide-react';
import {
  LayoutDashboard,
  User,
  Users,
  Clock,
  CheckSquare,
  Calendar,
  CalendarDays,
  X,
  ClipboardList,
  FileText,
  WalletCards,
  TrendingUp,
  Gift,
  ChevronLeft,
  ChevronRight,
  ScrollText,
} from 'lucide-react';

const Sidebar = ({ isOpen, onClose, collapsed = false, onToggleCollapse }) => {
  const menuItems = [
    { path: '/employee/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/employee/profile', icon: User, label: 'Your Profile' },
    { path: '/employee/my-team', icon: Users, label: 'My Team' },
    { path: '/employee/attendance', icon: Clock, label: 'Attendance Report' },
    { path: '/employee/tasks', icon: CheckSquare, label: 'My Assigned Tasks' },
    { path: '/employee/performance', icon: TrendingUp, label: 'My Performance' },
    { path: '/employee/leave-application', icon: Calendar, label: 'Leave Application' },
    { path: '/employee/comp-off', icon: Gift, label: 'Comp Off' },
    { path: '/employee/wfh', icon: HomeIcon, label: 'Work From Home' },
    { path: '/employee/holidays', icon: CalendarDays, label: 'Holidays' },
    { path: '/employee/attendance-requests', icon: ClipboardList, label: 'Regularization Requests' },
    { path: '/employee/payslips', icon: FileText, label: 'My Payslips' },
    { path: '/employee/policies', icon: ScrollText, label: 'Company Policies' },
    { path: '/employee/full-and-final', icon: WalletCards, label: 'Full And Final Settlement' },
  ];

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40 bg-slate-950/60 backdrop-blur-[2px] lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-50 flex flex-col border-r border-slate-200 bg-gradient-to-b from-white via-slate-50 to-white text-slate-800 shadow-[0_18px_45px_rgba(15,23,42,0.08)] transform transition-all duration-300 ease-out lg:translate-x-0 lg:static lg:inset-0 lg:flex-shrink-0
        ${collapsed ? 'lg:w-20' : 'lg:w-72'}
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className={`flex items-center gap-3 border-b border-slate-200 px-4 ${collapsed ? 'h-16 justify-center lg:px-3' : 'h-[72px] justify-between'}`}>
          <div className={`min-w-0 ${collapsed ? 'lg:hidden' : ''}`}>
            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-sky-500">Employee Portal</p>
            <h2 className="truncate text-base font-semibold text-slate-900">Workspace</h2>
          </div>

          <div className="flex items-center gap-2">
            {onToggleCollapse && (
              <button
                onClick={onToggleCollapse}
                className="inline-flex rounded-xl border border-slate-200 bg-white p-2 text-slate-600 shadow-sm transition hover:border-sky-300 hover:text-sky-700 hover:shadow-md"
                title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
              </button>
            )}
            <button
              onClick={onClose}
              className="lg:hidden rounded-xl border border-slate-200 bg-white p-2 text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-900"
              title="Close sidebar"
              aria-label="Close sidebar"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <nav className={`min-h-0 flex-1 overflow-y-auto px-3 py-4 ${collapsed ? 'lg:px-2' : 'lg:px-4'}`}>
          <ul className="space-y-1.5">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    className={({ isActive }) =>
                      `group relative flex items-center rounded-2xl px-3 py-3 text-sm font-medium transition-all duration-200 ${
                        isActive
                          ? 'bg-sky-50 text-slate-900 shadow-[inset_0_1px_0_rgba(255,255,255,0.7),0_8px_20px_rgba(14,165,233,0.10)] ring-1 ring-sky-200'
                          : 'text-slate-600 hover:-translate-y-0.5 hover:bg-slate-100 hover:text-slate-900 hover:shadow-[0_10px_20px_rgba(15,23,42,0.06)]'
                      }`
                    }
                    onClick={onClose}
                    title={collapsed ? item.label : undefined}
                  >
                    {({ isActive }) => (
                      <>
                        <span className="absolute inset-y-2 left-0 w-1 rounded-r-full bg-sky-500 opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
                        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-all duration-200 ${collapsed ? 'lg:mx-auto' : ''} ${isActive ? 'bg-white text-sky-700' : 'bg-slate-100 text-slate-500 group-hover:bg-white group-hover:text-sky-600'}`}>
                          <Icon className="h-5 w-5" />
                        </span>
                        <span className={`ml-3 min-w-0 truncate transition-all duration-200 ${collapsed ? 'lg:hidden' : ''}`}>
                          {item.label}
                        </span>
                      </>
                    )}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="border-t border-slate-200 px-4 py-4" />
      </div>
    </>
  );
};

export default Sidebar;
